import React from "react";
import { motion } from "framer-motion";
import { CalendarCheck, MessageCircle, ShieldCheck, HeartHandshake } from "lucide-react";

const CALENDLY = "https://calendly.com/wordgumbolearningco";

export default function Consultation() {
  return (
    <section
      id="consultation"
      data-testid="consultation-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative rounded-[2.4rem] overflow-hidden bg-gradient-to-br from-wg-purple via-wg-purpleDeep to-[#2A0D30] text-white p-10 md:p-16 shadow-[0_30px_80px_-20px_rgba(74,29,82,0.5)]"
        >
          {/* Decorative letters — hidden on mobile to avoid overlapping copy */}
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden hidden md:block">
            <span className="absolute top-8 right-10 text-7xl font-heading font-extrabold text-wg-yellow/30">A</span>
            <span className="absolute bottom-8 left-10 text-6xl font-heading font-extrabold text-wg-teal/30">b</span>
            <span className="absolute top-1/2 right-1/3 text-5xl font-heading font-extrabold text-wg-orange/30">c</span>
            <span className="absolute bottom-12 right-24 text-4xl font-heading font-extrabold text-wg-green/30">★</span>
          </div>

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3.5 py-1.5 border border-white/20">
                <span className="w-1.5 h-1.5 rounded-full bg-wg-yellow" />
                <span className="text-xs font-semibold tracking-[0.18em] uppercase">Free Consultation</span>
              </div>
              <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl xl:text-6xl leading-tight tracking-tight">
                Start with a <span className="text-wg-yellow">free consultation.</span>
              </h2>
              <p className="mt-5 text-lg md:text-xl text-white/85 max-w-2xl">
                Every child's learning journey is different. A free consultation gives families the
                opportunity to ask questions, share concerns, and learn which service may be the
                best fit for their child.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={CALENDLY}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="consultation-schedule-btn"
                  className="inline-flex items-center gap-2 bg-wg-yellow text-wg-ink rounded-full px-7 py-4 font-semibold hover:-translate-y-0.5 shadow-lg shadow-wg-yellow/30 transition-all"
                >
                  <CalendarCheck className="w-5 h-5" />
                  Schedule Your Free Consultation
                </a>
                <a
                  href="#contact"
                  data-testid="consultation-message-btn"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white rounded-full px-7 py-4 font-semibold hover:bg-white/15 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  Send a Message Instead
                </a>
              </div>
            </div>

            <ul className="lg:col-span-5 grid gap-4">
              {[
                { icon: ShieldCheck, title: "No pressure, ever", text: "A friendly conversation about your child's reading journey." },
                { icon: HeartHandshake, title: "Parent-friendly guidance", text: "Clear next steps, whether you book a service or not." },
                { icon: CalendarCheck, title: "Easy to book", text: "Pick a time that fits your family — fully online via Calendly." },
              ].map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex gap-4 rounded-2xl bg-white/8 backdrop-blur border border-white/10 p-5">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-wg-yellow text-wg-ink shrink-0">
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="font-heading font-bold text-lg">{title}</div>
                    <div className="text-white/80 text-sm leading-relaxed">{text}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
